import { chapterLanes, dragInterval } from "../player/annotationEditing";
import { activityLevel, isActivityLabel } from "../player/activityLabels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Annotation, WaveformData } from "../api/types";
import { NS, clamp, formatTimecode } from "../player/time";

/**
 * The common timeline.
 *
 * Everything drawn here is in session nanoseconds, so a pixel column means the
 * same instant on every row: the two eyes' frame coverage, the pair-delta
 * ribbon, the audio envelope and the annotations. Zoom and pan change the
 * window, never the mapping.
 */

export interface TimelineTrackData {
  leftFrames: number[];
  rightFrames: number[];
  waveform: WaveformData | null;
  syncSeries: { left_ns: number[]; delta_ms: number[]; valid: boolean[] } | null;
  maxPairDeltaMs: number;
}

const ROW = {
  ruler: 20,
  eye: 14,
  sync: 28,
  audio: 44,
  audioExtra: 22,
  lane: 32,
};

export function Timeline({
  rangeNs,
  viewNs,
  setViewNs,
  masterNs,
  onSeek,
  data,
  annotations,
  selectedId,
  onSelect,
  onCreateInterval,
  onUpdateInterval,
  loop,
  setLoop,
}: {
  rangeNs: [number, number];
  viewNs: [number, number];
  setViewNs: (v: [number, number]) => void;
  masterNs: number;
  onSeek: (ns: number) => void;
  data: TimelineTrackData;
  annotations: Annotation[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onCreateInterval: (startNs: number, endNs: number) => void;
  onUpdateInterval: (id: string, patch: Partial<Annotation>) => Promise<void>;
  loop: [number, number] | null;
  setLoop: (v: [number, number] | null) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(1000);
  const [drag, setDrag] = useState<null | { mode: "scrub" | "range"; from: number; to: number }>(null);
  const [edit, setEdit] = useState<null | {id:string;mode:"start"|"end"|"move";origin:number;start:number;end:number;preview:{start_ns:number;end_ns:number};bounds:[number,number]}>(null);
  const editRef = useRef(edit); editRef.current = edit;
  const [hoverNs, setHoverNs] = useState<number | null>(null);

  // Context and activity have stable lanes; colour still identifies provenance.
  const lanes = useMemo(() => chapterLanes(annotations), [annotations]);

  // Each audio channel gets its own lane, so a two-channel track is twice as
  // tall rather than two half-height traces sharing one.
  const audioChannels = Math.max(1, data.waveform?.channels ?? 1);
  const audioHeight = ROW.audio + (audioChannels - 1) * ROW.audioExtra;
  const height = ROW.ruler + ROW.eye * 2 + ROW.sync + audioHeight + lanes.length * ROW.lane + 6;

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const span = Math.max(1, viewNs[1] - viewNs[0]);
  const toX = useCallback((ns: number) => ((ns - viewNs[0]) / span) * width, [viewNs, span, width]);
  const toNs = useCallback((x: number) => viewNs[0] + (x / Math.max(1, width)) * span, [viewNs, span, width]);

  /* ---------------- drawing ---------------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.floor(height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const css = getComputedStyle(document.documentElement);
    const c = (name: string) => css.getPropertyValue(name).trim() || "#888";

    /* ruler */
    const targetTicks = Math.max(2, Math.floor(width / 110));
    const rawStep = span / targetTicks;
    const niceSteps = [
      0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 1800, 3600,
    ].map((s) => s * NS);
    const step = niceSteps.find((s) => s >= rawStep) ?? niceSteps[niceSteps.length - 1];
    ctx.font = "10px ui-monospace, monospace";
    ctx.fillStyle = c("--bg-1");
    ctx.fillRect(0, 0, width, ROW.ruler);
    ctx.strokeStyle = c("--line");
    ctx.beginPath();
    ctx.moveTo(0, ROW.ruler - 0.5);
    ctx.lineTo(width, ROW.ruler - 0.5);
    ctx.stroke();
    const first = Math.ceil(viewNs[0] / step) * step;
    ctx.fillStyle = c("--text-2");
    for (let t = first; t <= viewNs[1]; t += step) {
      const x = Math.round(toX(t)) + 0.5;
      ctx.strokeStyle = c("--line");
      ctx.beginPath();
      ctx.moveTo(x, ROW.ruler - 5);
      ctx.lineTo(x, ROW.ruler);
      ctx.stroke();
      ctx.fillText(formatTimecode(t, { ms: step < NS }), x + 3, 11);
    }

    let y = ROW.ruler + 2;

    /* per-eye frame coverage: a column is lit where that eye has frames */
    const drawCoverage = (times: number[], color: string, label: string) => {
      ctx.fillStyle = c("--bg-2");
      ctx.fillRect(0, y, width, ROW.eye - 2);
      if (times.length > 1) {
        ctx.fillStyle = color;
        // Bucket by pixel: dense recordings would otherwise draw 100k rects.
        const buckets = new Uint8Array(Math.max(1, Math.ceil(width)));
        for (const t of times) {
          if (t < viewNs[0] || t > viewNs[1]) continue;
          const x = Math.floor(toX(t));
          if (x >= 0 && x < buckets.length) buckets[x] = 1;
        }
        for (let x = 0; x < buckets.length; x++) if (buckets[x]) ctx.fillRect(x, y, 1, ROW.eye - 2);
      }
      ctx.fillStyle = c("--text-3");
      ctx.font = "9px ui-monospace, monospace";
      ctx.fillText(label, 4, y + ROW.eye - 5);
      y += ROW.eye;
    };
    drawCoverage(data.leftFrames, c("--left-eye"), "L");
    drawCoverage(data.rightFrames, c("--right-eye"), "R");

    /* pair-delta ribbon */
    ctx.fillStyle = c("--bg-2");
    ctx.fillRect(0, y, width, ROW.sync - 2);
    const limit = Math.max(data.maxPairDeltaMs, 1);
    const mid = y + (ROW.sync - 2) / 2;
    ctx.strokeStyle = c("--line-strong");
    ctx.beginPath();
    ctx.moveTo(0, mid + 0.5);
    ctx.lineTo(width, mid + 0.5);
    ctx.stroke();
    if (data.syncSeries) {
      const { left_ns, delta_ms, valid } = data.syncSeries;
      for (let i = 0; i < left_ns.length; i++) {
        const t = left_ns[i];
        if (t < viewNs[0] || t > viewNs[1]) continue;
        const x = toX(t);
        const norm = clamp(delta_ms[i] / limit, -1.6, 1.6);
        const h = (norm * (ROW.sync - 6)) / 2;
        ctx.fillStyle = valid[i] ? c("--good") : c("--bad");
        ctx.fillRect(x, mid, 1, -h || -0.5);
      }
    }
    ctx.fillStyle = c("--text-3");
    ctx.fillText(`Δ ±${limit.toFixed(1)}ms`, 4, y + 9);
    y += ROW.sync;

    /* audio envelope — one lane per channel.
       Drawing every channel separately is the point: a viewer can see whether
       a "stereo" track actually carries two signals rather than being told. */
    const wf = data.waveform;
    const laneCount = audioChannels;
    ctx.fillStyle = c("--bg-2");
    ctx.fillRect(0, y, width, audioHeight - 2);
    if (wf && wf.channel_peaks?.length) {
      const laneHeight = (audioHeight - 2) / laneCount;
      const nsPerBin = NS / wf.bins_per_second;
      for (let ch = 0; ch < laneCount; ch++) {
        const peaks = wf.channel_peaks[ch] ?? wf.channel_peaks[0];
        const centre = y + laneHeight * (ch + 0.5);
        const halfHeight = Math.max(2, laneHeight / 2 - 2);
        // A dual-mono second channel is drawn dimmed: it is real data, but
        // repeating the first channel at full strength would suggest content
        // that is not there.
        ctx.fillStyle = ch > 0 && wf.analysis?.identical ? c("--text-3") : c("--audio");
        for (let x = 0; x < width; x++) {
          const b0 = Math.floor((toNs(x) - wf.start_ns) / nsPerBin);
          const b1 = Math.ceil((toNs(x + 1) - wf.start_ns) / nsPerBin);
          if (b1 < 0 || b0 >= peaks.peak_max.length) continue;
          let lo = 0;
          let hi = 0;
          for (let b = Math.max(0, b0); b < Math.min(peaks.peak_max.length, Math.max(b1, b0 + 1)); b++) {
            lo = Math.min(lo, peaks.peak_min[b] / 127);
            hi = Math.max(hi, peaks.peak_max[b] / 127);
          }
          const top = centre - hi * halfHeight;
          const bottom = centre - lo * halfHeight;
          ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
        }
        if (laneCount > 1) {
          ctx.fillStyle = c("--text-3");
          ctx.fillText(`ch${ch}`, width - 26, centre + 3);
        }
      }
    }
    ctx.fillStyle = c("--text-3");
    ctx.fillText(
      wf
        ? `audio · ${wf.channels}ch ${Math.round(wf.sample_rate / 1000)}kHz` +
          (wf.analysis?.verdict === "dual_mono" ? " · dual-mono (measured)" : "")
        : "no audio",
      4,
      y + 9,
    );
    y += audioHeight;

    /* annotation lane backgrounds */
    for (const lane of lanes) {
      ctx.fillStyle = c("--bg-2");
      ctx.fillRect(0, y, width, ROW.lane - 2);
      ctx.fillStyle = c("--text-3");
      ctx.fillText(lane.label, 4, y + ROW.lane - 7);
      y += ROW.lane;
    }

    /* loop range */
    if (loop) {
      ctx.fillStyle = "rgba(76,141,255,0.10)";
      ctx.fillRect(toX(loop[0]), ROW.ruler, toX(loop[1]) - toX(loop[0]), height - ROW.ruler);
      ctx.strokeStyle = c("--accent");
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(toX(loop[0]) + 0.5, ROW.ruler);
      ctx.lineTo(toX(loop[0]) + 0.5, height);
      ctx.moveTo(toX(loop[1]) + 0.5, ROW.ruler);
      ctx.lineTo(toX(loop[1]) + 0.5, height);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    /* pending range drag */
    if (drag?.mode === "range") {
      const a = Math.min(drag.from, drag.to);
      const b = Math.max(drag.from, drag.to);
      ctx.fillStyle = "rgba(176,122,232,0.18)";
      ctx.fillRect(toX(a), ROW.ruler, toX(b) - toX(a), height - ROW.ruler);
    }

  }, [width, height, audioHeight, audioChannels, viewNs, span, data.leftFrames, data.rightFrames,
      data.waveform, data.syncSeries, data.maxPairDeltaMs, lanes, loop, drag, toX]);

  /* ---------------- interaction ---------------- */
  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const rect = hostRef.current!.getBoundingClientRect();
      const anchor = toNs(e.clientX - rect.left);
      if (e.shiftKey) {
        const shift = (e.deltaY / width) * span;
        const lo = clamp(viewNs[0] + shift, rangeNs[0], rangeNs[1]);
        setViewNs([lo, lo + span]);
        return;
      }
      const factor = Math.exp(e.deltaY * 0.0016);
      const newSpan = clamp(span * factor, 20_000_000, rangeNs[1] - rangeNs[0]);
      const ratio = (anchor - viewNs[0]) / span;
      let lo = anchor - ratio * newSpan;
      let hi = lo + newSpan;
      if (lo < rangeNs[0]) { lo = rangeNs[0]; hi = lo + newSpan; }
      if (hi > rangeNs[1]) { hi = rangeNs[1]; lo = hi - newSpan; }
      setViewNs([Math.max(rangeNs[0], lo), Math.min(rangeNs[1], hi)]);
    },
    [toNs, span, viewNs, rangeNs, setViewNs, width],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const rect = hostRef.current!.getBoundingClientRect();
      const ns = toNs(e.clientX - rect.left);
      // alt-drag marks out an interval; a plain drag scrubs.
      const mode = e.altKey ? "range" : "scrub";
      setDrag({ mode, from: ns, to: ns });
      if (mode === "scrub") onSeek(ns);
    },
    [toNs, onSeek],
  );

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      const rect = hostRef.current!.getBoundingClientRect();
      const ns = clamp(toNs(e.clientX - rect.left), rangeNs[0], rangeNs[1]);
      setDrag((d) => (d ? { ...d, to: ns } : d));
      if (drag.mode === "scrub") onSeek(ns);
    };
    const onUp = () => {
      if (drag.mode === "range" && Math.abs(drag.to - drag.from) > 1_000_000) {
        onCreateInterval(Math.min(drag.from, drag.to), Math.max(drag.from, drag.to));
      }
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, toNs, onSeek, onCreateInterval, rangeNs]);

  useEffect(() => {
    if (!edit) return;
    const move = (e: MouseEvent) => {
      const d = editRef.current;
      if (!d || !hostRef.current) return;
      const ns = toNs(e.clientX-hostRef.current.getBoundingClientRect().left);
      let delta = ns-d.origin;
      if (!e.shiftKey) {
        const edge = (d.mode === "end" ? d.end : d.start)+delta;
        const targets = [masterNs,...rangeNs,...annotations.filter(a=>a.id!==d.id).flatMap(a=>[a.start_ns,a.end_ns??a.start_ns])];
        const nearest = targets.sort((a,b)=>Math.abs(a-edge)-Math.abs(b-edge))[0];
        if (Math.abs(toX(nearest)-toX(edge)) < 8) delta += nearest-edge;
      }
      const next = {...d,preview:dragInterval(d.start,d.end,delta,d.mode,d.bounds)};
      editRef.current=next;setEdit(next);
    };
    const up = () => {
      const d = editRef.current;editRef.current=null;setEdit(null);
      if (d && (d.preview.start_ns!==d.start || d.preview.end_ns!==d.end)) void onUpdateInterval(d.id,d.preview);
    };
    const cancel = (e:KeyboardEvent) => {if(e.key === "Escape") {editRef.current=null;setEdit(null);}};
    window.addEventListener("mousemove",move);window.addEventListener("mouseup",up);window.addEventListener("keydown",cancel);
    return () => {window.removeEventListener("mousemove",move);window.removeEventListener("mouseup",up);window.removeEventListener("keydown",cancel);};
  }, [!!edit,toNs,toX,masterNs,annotations,rangeNs,onUpdateInterval]);

  const beginEdit = (e:React.MouseEvent,a:Annotation,mode:"start"|"end"|"move") => {
    e.stopPropagation();e.preventDefault();onSelect(a.id);
    if (a.end_ns == null) {onSeek(a.start_ns);return;}
    const peers=annotations.filter(other=>other.id!==a.id && isActivityLabel(other) && other.review_status!=="rejected" && activityLevel(other)===activityLevel(a));
    const before=peers.filter(o=>(o.end_ns??o.start_ns)<=a.start_ns).map(o=>o.end_ns??o.start_ns);
    const after=peers.filter(o=>o.start_ns>=a.end_ns!).map(o=>o.start_ns);
    const bounds:[number,number]=[Math.max(rangeNs[0],...before),Math.min(rangeNs[1],...after)];
    const value = {id:a.id,mode,bounds,origin:toNs(e.clientX-hostRef.current!.getBoundingClientRect().left),start:a.start_ns,end:a.end_ns,
      preview:{start_ns:a.start_ns,end_ns:a.end_ns}};
    editRef.current=value;setEdit(value);
  };

  const laneTop = ROW.ruler + ROW.eye * 2 + ROW.sync + audioHeight;

  return (
    <div
      ref={hostRef}
      style={{ position: "relative", width: "100%", height, userSelect: "none", cursor: "text" }}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={(e) => {
        const rect = hostRef.current!.getBoundingClientRect();
        setHoverNs(toNs(e.clientX - rect.left));
      }}
      onMouseLeave={() => setHoverNs(null)}
      onDoubleClick={() => setViewNs([rangeNs[0], rangeNs[1]])}
    >
      <canvas ref={canvasRef} style={{ width: "100%", height, display: "block" }} />
      <div aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0,
        left: toX(masterNs), borderLeft: "1px solid var(--accent)", pointerEvents: "none" }} />

      {lanes.map((lane, li) =>
        lane.items.map((a) => {
          const shown = edit?.id === a.id ? {...a,...edit.preview} : a;
          const x0 = Math.max(0,toX(shown.start_ns));
          const x1 = shown.end_ns != null ? Math.min(width,toX(shown.end_ns)) : x0;
          if (shown.start_ns > viewNs[1] || (shown.end_ns ?? shown.start_ns) < viewNs[0]) return null;
          if (x1 < -20 || x0 > width + 20) return null;
          const isPoint = a.end_ns == null;
          const colour = a.source === "model" ? "var(--model)" : a.source === "imported" ? "var(--imported)" : "var(--manual)";
          const rejected = a.review_status === "rejected";
          return (
            <div
              key={a.id}
              title={`${a.kind}${a.label ? ` · ${a.label}` : ""} · ${a.source}${a.review_status !== "unreviewed" ? ` · ${a.review_status}` : ""}`}
              className="timeline-chapter"
              data-annotation-id={a.id}
              aria-label={`${lane.label}: ${a.label}`}
              onDoubleClick={e=>{e.stopPropagation();onSeek(a.start_ns);}}
              onMouseDown={e => beginEdit(e,a,"move")}
              style={{
                position: "absolute",
                left: isPoint ? x0 - 4 : x0,
                width: isPoint ? 8 : Math.max(3, x1 - x0),
                top: laneTop + li * ROW.lane + 2,
                height: ROW.lane - 6,
                background: rejected ? "transparent" : colour,
                opacity: rejected ? 0.5 : selectedId === a.id ? 1 : 0.82,
                border: selectedId === a.id ? "1px solid var(--text-0)" : `1px solid ${colour}`,
                borderRadius: isPoint ? 2 : 3,
                cursor: isPoint ? "pointer" : "grab",
                overflow: "hidden",
                fontSize: 11,
                lineHeight: `${ROW.lane - 8}px`,
                padding: 0,
                color: "#04122c",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxSizing: "border-box",
              }}
            >
              {!isPoint && <>
                <button className="timeline-handle start" aria-label={`Resize start: ${a.label}`} title="Drag start · Shift disables snapping" onMouseDown={e=>beginEdit(e,a,"start")}/>
                <button className="timeline-handle end" aria-label={`Resize end: ${a.label}`} title="Drag end · Shift disables snapping" onMouseDown={e=>beginEdit(e,a,"end")}/>
              </>}
              {!isPoint && x1 - x0 > 40 && <span style={{position:"absolute",left:12,right:12,overflow:"hidden",textOverflow:"ellipsis",pointerEvents:"none"}}>{a.label || a.kind}</span>}
            </div>
          );
        }),
      )}

      {edit && <div className="timeline-edit-readout">{formatTimecode(edit.preview.start_ns)} → {formatTimecode(edit.preview.end_ns)}</div>}
      {hoverNs != null && !edit ? (
        <div
          style={{
            position: "absolute", top: 0, left: clamp(toX(hoverNs), 0, width - 76),
            background: "var(--bg-3)", border: "1px solid var(--line-strong)", borderRadius: 3,
            padding: "1px 4px", fontSize: 10, fontFamily: "var(--mono)", pointerEvents: "none",
            color: "var(--text-1)",
          }}
        >
          {formatTimecode(hoverNs)}
        </div>
      ) : null}

      {loop ? (
        <button
          className="ghost"
          style={{ position: "absolute", right: 4, top: 1, fontSize: 11, padding: "1px 5px" }}
          onMouseDown={(e) => { e.stopPropagation(); setLoop(null); }}
        >
          clear loop
        </button>
      ) : null}
    </div>
  );
}
